import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SparklesIcon,
  XMarkIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { isPaidUser } from '../../lib/plan';
import { PLANBASE_CONFIG } from '../../config';
import MarkdownContent from './MarkdownContent';
import { cn } from '../../lib/utils';

/**
 * Floating Orbi assistant for the upload page only.
 *
 * Dedicated minimal composer — intentionally NOT the main-page
 * ChatComposer. The user complaint was that it looked like the
 * same thing twice. This one has: a text field, a help label, a
 * send pill. No mode pills, no voice input, no file attach — all
 * that lives on the main composer where it belongs.
 *
 * Pro-only. Returns null for free-tier users entirely so there's
 * no persistent locked affordance on the page.
 */

interface Turn {
  role: 'user' | 'assistant';
  content: string;
}

const STORAGE_KEY = 'planbase_floating_orbi_open';

const SUGGESTIONS = [
  'Suggest a tighter title',
  'Rewrite my description, warmer tone',
  'What tags fit this project?',
  'Draft a short architect brief',
];

export default function FloatingOrbi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPaid = isPaidUser(user);
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [prompt, setPrompt] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  }, [open]);

  // Auto-focus when opened; scroll to bottom on new turn.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, busy]);

  if (!user) return null;

  const send = async (text?: string) => {
    const value = (text ?? prompt).trim();
    if (!value || busy) return;
    // Free-tier users can see Orbi and type a prompt — the moment they
    // try to send it, we surface the upgrade panel instead of hitting
    // the AI endpoint. Keeps the whisper of Pro in front of them
    // without the lecture of a locked composer.
    if (!isPaid) {
      setShowPaywall(true);
      return;
    }
    const nextTurns: Turn[] = [...turns, { role: 'user', content: value }];
    setTurns(nextTurns);
    setPrompt('');
    setBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('planbase_token');
      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
          'X-User-Token': token || '',
        },
        body: JSON.stringify({
          action: 'enhance_project',
          prompt: value,
          mode: 'portfolio',
          useArchitectData: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Orbi error (${res.status})`);
      setTurns([...nextTurns, { role: 'assistant', content: data.result || 'No answer' }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Orbi error');
      setTurns(nextTurns);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open Orbi help"
          className={cn(
            'fixed z-40 flex items-center gap-2 rounded-full px-4 h-11 shadow-lg transition-all',
            'bottom-20 right-4 md:bottom-5 md:right-5',
            'bg-primary text-primary-foreground hover:scale-105',
          )}
        >
          <SparklesIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Need help?</span>
        </button>
      )}

      {open && (
        <div
          className={cn(
            'fixed z-50 flex flex-col rounded-2xl border border-border bg-card shadow-2xl',
            'bottom-20 right-4 md:bottom-5 md:right-5',
            'w-[calc(100vw-2rem)] max-w-[380px] h-[min(520px,80vh)]',
          )}
          role="dialog"
          aria-label="Orbi help assistant"
        >
          <header className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                <SparklesIcon className="h-3.5 w-3.5" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-medium text-foreground">Orbi</span>
                <span className="text-[10px] text-muted-foreground">Writing help for this upload</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Orbi"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {turns.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground px-1">
                  Paste a field and ask Orbi to tighten it, lengthen it, or suggest tags.
                </p>
                <div className="grid grid-cols-1 gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="text-left text-xs px-3 py-2 rounded-lg border border-border bg-background hover:border-primary hover:bg-muted transition-colors text-foreground"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {turns.map((t, i) => (
              <div key={i} className={t.role === 'user' ? 'ml-6' : 'mr-6'}>
                {t.role === 'user' ? (
                  <div className="rounded-xl bg-primary/10 text-foreground text-sm px-3 py-2 ml-auto w-fit max-w-full whitespace-pre-wrap">
                    {t.content}
                  </div>
                ) : (
                  <div className="rounded-xl bg-muted/50 px-3 py-2">
                    <MarkdownContent content={t.content} />
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="mr-6">
                <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  Orbi is thinking…
                </div>
              </div>
            )}
            {error && (
              <p role="alert" className="text-xs text-destructive text-center">{error}</p>
            )}
          </div>

          <div className="border-t border-border p-2.5">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                rows={1}
                placeholder="Ask about this upload…"
                className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary max-h-28"
                aria-label="Ask Orbi"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={!prompt.trim() || busy}
                aria-label="Send to Orbi"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-muted-foreground/40"
              >
                {busy
                  ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  : <PaperAirplaneIcon className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between pt-2 px-1">
              <span className="text-[10px] text-muted-foreground">
                {isPaid ? 'Counts toward your AI usage' : 'Preview — upgrade to send'}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                <LockClosedIcon className="h-3 w-3" />
                Pro
              </span>
            </div>
          </div>

          {/* Free-tier paywall overlay — shown only after a send attempt
              so the UI stays unobtrusive until the user actually asks
              Orbi to do something. */}
          {showPaywall && !isPaid && (
            <div className="absolute inset-0 z-10 rounded-2xl bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary mb-3">
                <SparklesIcon className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1.5">Orbi is a Pro feature</p>
              <p className="text-xs text-muted-foreground mb-5 max-w-[260px]">
                Upgrade to send prompts, get title and tag suggestions, and rewrite your project copy.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setShowPaywall(false); navigate('/pricing'); }}
                  className="inline-flex h-9 items-center justify-center rounded-full bg-primary text-primary-foreground px-5 text-xs font-medium hover:opacity-90 transition-opacity"
                >
                  Upgrade to Pro
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaywall(false)}
                  className="inline-flex h-9 items-center justify-center rounded-full border border-border text-foreground px-4 text-xs font-medium hover:bg-muted transition-colors"
                >
                  Not now
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
